// formatters.js
import React from "react";

const getMicrosecondsDivisorAndUnits = value => {
  const maxValue = Math.abs(value / 1e3);

  let divisor, units;

  if (maxValue >= 1000) {
    divisor = 1e6;
    units = "sec";
  } else if (maxValue > 0.1) {
    divisor = 1e3;
    units = "ms";
  } else {
    divisor = 1;
    units = "\u03BCs";
  }

  return { divisor, units };
};

// Provide two outputs:
// - `toString()` with a space between the value and the units
// - `toJSX()` with value and units in identifiable spans within a wrapper span
export class FormattedOutput {
  constructor(formattedValue) {
    this.formattedValue = formattedValue;
  }

  toString(delimiter = " ") {
    return `${this.formattedValue.value}${delimiter}${this.formattedValue
      .units}`;
  }

  raw() {
    const { value, units } = this.formattedValue;
    return { value, units };
  }

  toJSX() {
    return (
      <span>
        <span className="value">{this.formattedValue.value}</span>
        &nbsp;
        <span className="units">{this.formattedValue.units}</span>
      </span>
    );
  }
}

export const NO_VALUE = new class {
  toString() {
    return "";
  }
  raw() {
    return { value: undefined, units: undefined };
  }
  toJSX() {
    return "";
  }
}();

/**
 * This is a refactored port of previous functionality:
 *
 *   https://github.com/librato/tracelons/blob/05c8c3aa2120c823e14ea6bfd86eb72ceecea3d6/tracelytics/tracelytics/public/js/graphs.js#L591-L615
 *
 * This purpose of this function is to provide intelligent formatting of
 * ticks on a chart axis. More context:
 *
 *   https://swicloud.slack.com/archives/C2DN8D4R4/p1493054125173138
 */
export const formatMillisecondsForAxis = stepSize => {
  const formatMicroseconds = formatMicrosecondsForAxis(stepSize);
  return value => formatMicroseconds(value && value * 1e3);
};

export const formatMicrosecondsForAxis = stepSize => {
  return value => {
    if (value === undefined || value === null || value === 0) {
      return "";
    }

    // this is necessary to account for float multiplication issues
    const log10rounded = n => {
      return Math.floor(
        parseFloat((Math.log(Math.abs(n)) / Math.LN10).toPrecision(12))
      );
    };

    const { divisor, units } = getMicrosecondsDivisorAndUnits(value);
    const numOfDecimals = Math.max(0, -log10rounded(stepSize / divisor));

    return `${(value / divisor).toFixed(numOfDecimals)} ${units}`;
  };
};

export const formatMillisecondsForDisplay = (value, numOfDecimals = 2) => {
  return formatMicrosecondsForDisplay(value && value * 1e3, numOfDecimals);
};

export const formatMicrosecondsForDisplay = (value, numOfDecimals = 2) => {
  if (value === undefined || value === null || value === 0) {
    return NO_VALUE;
  }

  const { divisor, units } = getMicrosecondsDivisorAndUnits(value);
  const microseconds = (value / divisor).toFixed(numOfDecimals);

  return new FormattedOutput({
    value: microseconds,
    units,
  });
};

export const formatPercentageForDisplay = (value, numOfDecimals = 2) => {
  if (value === undefined || value === null) {
    return NO_VALUE;
  }
  return new FormattedOutput({
    value: value.toFixed(numOfDecimals),
    units: "%",
  });
};
